import { useApiClient } from '@brewardapp/api-client';
import { useTranslation } from 'react-i18next';

interface DescriptorEntry {
  id?: string;
  value: string;
  intensity?: string | number;
}

interface DescriptorRowProps {
  descriptor: DescriptorEntry;
  withIntensity?: boolean;
}

const capitalize = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

const DescriptorRow = ({ descriptor, withIntensity }: DescriptorRowProps) => {
  const api = useApiClient();
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    descriptor.value,
  );

  const { data: descriptorData, isLoading } = api.descriptors.useFindDescriptor(
    { filter: { id: descriptor.value } },
    { enabled: isUUID },
  ) as { data: { data?: { name?: string } } | undefined; isLoading: boolean };

  const rawName = isUUID ? descriptorData?.data?.name : descriptor.value;
  const name = rawName ? capitalize(rawName) : undefined;

  if (isLoading && isUUID) {
    return (
      <div className="flex w-full items-center justify-between gap-6 py-2">
        <div className="h-4 w-32 animate-pulse rounded bg-content-200 dark:bg-content-700" />
        <div className="h-10 w-10 animate-pulse rounded-full bg-content-200 dark:bg-content-700" />
      </div>
    );
  }

  if (!name) return null;

  return (
    <div className="flex w-full items-center justify-between gap-6 py-2 border-b border-primary-100/60 dark:border-primary-800/40 last:border-0">
      {/* Label */}
      <div className="text-content-700 dark:text-content-300 font-sans text-base font-semibold">
        {name}
      </div>

      {/* Intensity badge */}
      {withIntensity && descriptor.intensity !== undefined ? (
        <div className="flex shrink-0 flex-col items-center gap-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary-400/50 bg-primary-50 dark:border-primary-600/50 dark:bg-primary-900/30 shadow-sm">
            <span className="font-header text-base font-bold text-primary-700 dark:text-primary-300">
              {descriptor.intensity}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-primary-200/50 bg-primary-50/50 dark:border-primary-700/30 dark:bg-primary-900/20">
          <div className="icon-[ph--check] h-4 w-4 text-primary-400 dark:text-primary-500" />
        </div>
      )}
    </div>
  );
};

interface DescriptorFieldProps {
  withIntensity?: boolean;
  group?: string;
  fieldSelector?: DescriptorEntry[];
}

const DescriptorField = ({ withIntensity, fieldSelector }: DescriptorFieldProps) => {
  const [t] = useTranslation();

  if (!fieldSelector || !Array.isArray(fieldSelector) || fieldSelector.length === 0) {
    return null;
  }

  const sorted = [...fieldSelector].sort((a, b) => {
    const aVal = a.intensity !== undefined ? Number(a.intensity) : -Infinity;
    const bVal = b.intensity !== undefined ? Number(b.intensity) : -Infinity;
    return bVal - aVal;
  });

  return (
    <div className="w-full py-3 space-y-1">
      <div className="text-primary-700 dark:text-primary-300 font-sans text-xs font-semibold uppercase tracking-widest mb-3">
        {t('Pages:ScoresheetPage.Descriptors', 'Descriptors')}
      </div>
      <div className="space-y-0 ml-4">
        {sorted.map((descriptor, index) => (
          <DescriptorRow
            key={descriptor.id || descriptor.value || index}
            descriptor={descriptor}
            withIntensity={withIntensity}
          />
        ))}
      </div>
    </div>
  );
};

export default DescriptorField;
